import './style.module.css';

import classNames from 'classnames';
import { GatsbyImage } from 'gatsby-plugin-image';
import React, { ReactNode } from 'react';

import type { BreadcrumbProps } from '../../components/breadcrumb';
import { NewsletterHero } from '../../components/newsletter-hero';
import { NewsletterHeroMailchimp } from '../../components/newsletter-hero-mailchimp';
import type { PageFooterProps } from '../../components/page-footer';
import { PageFooter } from '../../components/page-footer';
import type { PageHeaderProps } from '../../components/page-header';
import { PageHeader } from '../../components/page-header';
import { AuthProvider } from '../../contexts/auth';
import { useSiteConfig } from '../../fragments/site-config';
import {
	article as articleStyle,
	content,
	inner,
	landingPage as landingPageStyle,
	outer,
	spacerDiv as spacerDivStyle,
} from './style.module.css';

export interface DefaultLayoutProps {
	header?: PageHeaderProps;
	footer?: PageFooterProps;
	breadcrumb?: BreadcrumbProps;
	newsletterHero?: boolean;
	mailchimpInterestId?: string;
	landingPage?: boolean;
	searchInput?: boolean;
	children: ReactNode | ((siteConfigData: ReturnType<typeof useSiteConfig>) => ReactNode);
}

export const DefaultLayout = ({
	children,
	header,
	footer,
	breadcrumb,
	newsletterHero,
	mailchimpInterestId,
	landingPage,
	searchInput = true,
}: DefaultLayoutProps): JSX.Element => {
	const siteConfigData = useSiteConfig();
	if (!siteConfigData) return <p>Could not load site config</p>;

	const {
		headerLogo,
		footerLogo,
		navigationBarLinks,
		navigationMapLinksCompany,
		navigationMapLinksSocials,
		navigationMapLinksLegal,
		footerCopyrightText,
		newsletterHeroTitle,
		newsletterHeroSummary,
		newsletterHeroImage,
		newsletterHeroCallToActionTitle,
		newsletterHeroCallToActionTitleError,
		newsletterHeroTitleSubmitted,
		newsletterHeroSummarySubmitted,
		supportEmail,
		formErrorMessage,
	} = siteConfigData;

	return (
		<AuthProvider>
			<div className={outer}>
				<div className={inner}>
					<PageHeader
						logoImageSource={headerLogo?.url}
						logoAltText={headerLogo?.title}
						logoLinkHref="/"
						navItems={navigationBarLinks}
						breadcrumb={breadcrumb}
						searchInput={searchInput}
						{...header}
					/>
					<div
						className={classNames(
							landingPage && landingPageStyle,
							// @todo: separate prop?
							!!breadcrumb && articleStyle,
							content
						)}
					>
						{typeof children === 'function' ? children(siteConfigData) : children}
					</div>
					<div className={spacerDivStyle}></div>
					{newsletterHero && (
						<NewsletterHero
							title={newsletterHeroTitle ?? undefined}
							summary={newsletterHeroSummary ?? undefined}
							callToActionTitle={newsletterHeroCallToActionTitle ?? undefined}
							callToActionError={newsletterHeroCallToActionTitleError ?? undefined}
							titleSubmitted={newsletterHeroTitleSubmitted ?? undefined}
							summarySubmitted={newsletterHeroSummarySubmitted ?? undefined}
							supportEmail={supportEmail ?? undefined}
							errorMessage={formErrorMessage ?? undefined}
							renderImage={() =>
								!!newsletterHeroImage?.gatsbyImageData && (
									<GatsbyImage
										alt={newsletterHeroImage.title ?? 'Contact Us Image'}
										image={newsletterHeroImage.gatsbyImageData}
									/>
								)
							}
						/>
					)}
					{!!mailchimpInterestId && (
						<NewsletterHeroMailchimp
							mailchimpInterestId={mailchimpInterestId}
							title={newsletterHeroTitle ?? undefined}
							summary={newsletterHeroSummary ?? undefined}
							callToActionTitle={newsletterHeroCallToActionTitle ?? undefined}
							titleSubmitted={newsletterHeroTitleSubmitted ?? undefined}
							summarySubmitted={newsletterHeroSummarySubmitted ?? undefined}
							supportEmail={supportEmail ?? undefined}
							errorMessage={formErrorMessage ?? undefined}
							renderImage={() =>
								!!newsletterHeroImage?.gatsbyImageData && (
									<GatsbyImage
										alt={newsletterHeroImage.title ?? 'Contact Us Image'}
										image={newsletterHeroImage.gatsbyImageData}
									/>
								)
							}
						/>
					)}
					<PageFooter
						logoImageSource={footerLogo?.url}
						logoAltText={footerLogo?.title}
						logoLinkHref="/"
						copyright={footerCopyrightText}
						navItems={{
							company: navigationMapLinksCompany,
							legal: navigationMapLinksLegal,
							socials: navigationMapLinksSocials,
						}}
						newsletterHero={newsletterHero}
						{...footer}
					/>
				</div>
			</div>
		</AuthProvider>
	);
};

const makeTitle = (title?: string) => ['AIA', title].filter((value) => !!value).join(' - ');

export interface DefaultHeadProps {
	title?: string;
}

export const DefaultHead = ({ title }: DefaultHeadProps): JSX.Element => (
	<>
		<title>{makeTitle(title)}</title>

		{/* brother-1816 font */}
		<link rel="stylesheet" href="https://use.typekit.net/afb8rnr.css" />

		{/* avenir */}
		<link
			rel="preload"
			href="/fonts/AvenirNextLTProRegular/font.woff2"
			as="font"
			type="font/woff2"
			crossOrigin="anonymous"
		/>
		<link
			rel="preload"
			href="/fonts/AvenirNextLTProBold/font.woff2"
			as="font"
			type="font/woff2"
			crossOrigin="anonymous"
		/>
		<link
			rel="preload"
			href="/fonts/AvenirNextLTProMedium/font.woff2"
			as="font"
			type="font/woff2"
			crossOrigin="anonymous"
		/>
		<link
			rel="preload"
			href="/fonts/AvenirNextLTProRegular/font.woff"
			as="font"
			type="font/woff"
			crossOrigin="anonymous"
		/>
		<link
			rel="preload"
			href="/fonts/AvenirNextLTProBold/font.woff"
			as="font"
			type="font/woff"
			crossOrigin="anonymous"
		/>
		<link
			rel="preload"
			href="/fonts/AvenirNextLTProMedium/font.woff"
			as="font"
			type="font/woff"
			crossOrigin="anonymous"
		/>
	</>
);
